import { errorToast } from '@/utils';
import { Button, Divider, Flex } from 'antd';
import { useDecidePhotoPermissionMutation } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import type { EmployeeData } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.type';
import { Calendar03Icon } from '@/icons';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import useModal from '../../../../../../hooks/useModal';
import { cn } from '../../../../../../lib/cn';
import { toast } from '../../../../../../lib/toast';
import ClockIcon from '../Summery/_icons/ClockIcon';

interface EmpRequestProps {
  employee?: EmployeeData;
}

const EmpRequest = ({ employee }: EmpRequestProps) => {
  const { open, handleOpen, handleClose } = useModal<
    undefined,
    'APPROVE' | 'REJECT'
  >();
  const [decidePhotoPermission, { isLoading, error }] =
    useDecidePhotoPermissionMutation();

  const handleConfirm = async () => {
    if (!employee?.user_id || !open.type) return;

    const status = open.type === 'APPROVE' ? 'APPROVED' : 'REJECTED';

    try {
      await decidePhotoPermission({
        userId: employee.user_id,
        status,
      }).unwrap();

      toast('success', `Photo permission ${status.toLowerCase()} successfully`);
      handleClose();
    } catch (err) {
      errorToast(error, `Failed to ${status.toLowerCase()} photo permission.`);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <div className="bg-theme-slate-800 rounded-xl p-6">
        <h3 className="text-xl text-white font-medium mb-6">Request ( 01 )</h3>
        <Divider />

        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <Flex align="center" gap={10}>
              <div className="w-2.5 h-2.5 rounded-full bg-theme-primary" />
              <span className="text-theme-gray-300 text-sm">Request 1</span>
            </Flex>
            <h4 className="text-lg text-white font-medium">
              Profile Image Update Request
            </h4>
          </div>

          <div className="text-theme-gray-300 space-y-3">
            <Flex align="center" gap={10} justify="end">
              <Calendar03Icon size={20} className="text-theme-primary" />
              <span className="text-sm">
                {formatDate(employee?.photo_request_at)}
              </span>
            </Flex>
            <Flex align="center" gap={10} justify="end">
              <ClockIcon className="text-xl text-theme-primary" />
              <span className="text-sm font-medium">
                {formatTime(employee?.photo_request_at)}
              </span>
            </Flex>
          </div>
        </div>

        <Flex gap={16} className="mt-6">
          <Button
            onClick={() => handleOpen({ type: 'REJECT' })}
            className={cn('btn-primary-bordered !h-10 !px-10 !text-base')}
            disabled={isLoading}
          >
            Reject
          </Button>
          <Button
            onClick={() => handleOpen({ type: 'APPROVE' })}
            className={cn('btn-primary !h-10 !px-10 !text-base')}
            disabled={isLoading}
          >
            Approve
          </Button>
        </Flex>
      </div>

      <ConfirmationModal
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        handleSubmit={handleConfirm}
        open={open.open && (open.type === 'APPROVE' || open.type === 'REJECT')}
        loading={isLoading}
        title={`${open.type === 'APPROVE' ? 'Approve' : 'Reject'} Photo Permission?`}
        subTitle={`Are you sure you want to ${open.type === 'APPROVE' ? 'approve' : 'reject'} this photo permission request?`}
        buttonName="Confirm"
      />
    </>
  );
};

export default EmpRequest;
